import DOMPurify from 'dompurify';
import { postBoomiService, postSegmentService, postTrialInterestBoomiService } from '../../services/trackingServices';
import { gtagEnvelope } from '../gtagEnvelope';
import { FormData, SendAsyncPostObject, SitecoreTrialContentType } from '../../types';
import { getDataResidencyParam } from '../getDataResidencyParam';
import { getCookie } from '../cookies';
import { emailCheck } from '../emailCheck';
import { postTrialFormDataService, retainFormData } from '../../services/sitecoreServices';
import { getFlowType, getFlowTypePayload } from '../getFlowType';
import { FORM_TYPES, MODAL_TYPES, MODIFIER_TYPE, TRIAL_TYPES } from '../../consts';
import { getEventName, getEventReason, getExperimentVersion } from '../getExperimentVersion';
import { facebookCAPIGTMEvent } from '../facebookCAPIGTMEvent';
import { analyticsEvent } from '../analyticsEvent';
import { useAppStore } from '~/common/store/appStore';

interface StandardSubmission {
  data: SitecoreTrialContentType;
  existingUserEmail: string;
  values: SendAsyncPostObject;
  marketingOptOutSeen: boolean;
  contactPermission: boolean;
  webEnrichedFields?: string;
  contactToken?: string | null;
  signal?: AbortSignal;
}

export const handleStandardTrialSubmission = async ({
  data,
  existingUserEmail,
  values,
  marketingOptOutSeen,
  contactPermission,
  webEnrichedFields,
  contactToken,
  signal,
}: StandardSubmission) => {
  const { modifier, formType, trialLength, segExpQuery, segExpValue, isLoggedIn, trialType, businessMax } =
    useAppStore.getState();

  const flowType = getFlowType(formType);

  const is2Step = trialType === TRIAL_TYPES.BUSINESS && flowType !== FORM_TYPES.PURCHASE && !existingUserEmail;

  try {
    gtagEnvelope('event', 'cta_submit_click', {
      cta_name: `Start My Free ${trialLength}-Day Trial`,
      cta_url: `${window.location.origin}${window.location.pathname}${data.successUrl}`,
      cta_position: 'Trial Form',
    });

    if (modifier !== MODAL_TYPES.UNIFIED) {
      await postSegmentService({
        event: `${getEventName(true)}_trial_form_click`,
        properties: {
          reason: getEventReason(),
          ...(is2Step && { journey_step: `Step 2` }),
        },
      });
    }

    const userEmail = existingUserEmail || DOMPurify.sanitize(values.email.toString().toLowerCase());

    const fullName = values.fullName != null ? values.fullName.toString() : null;
    const [firstName, ...rest] = fullName ? fullName.split(' ') : [];
    const lastName = fullName ? rest.join(' ') : null;

    // gather form data
    const formData: FormData = {
      email: userEmail,
      firstName: fullName ? DOMPurify.sanitize(firstName) : null,
      lastName: lastName !== null ? DOMPurify.sanitize(lastName) : null,
      jobTitle: values.title && DOMPurify.sanitize(values.title.toString()),
      companyName: values.company != null ? DOMPurify.sanitize(values.company.toString()) : null,
      companySize: values.employees && values.employees.toString(),
      phoneNumber: values.temp_phone__c && DOMPurify.sanitize(values.temp_phone__c.toString()),
      trialSku: data.sku,
      marketingOptOutShown: marketingOptOutSeen,
      contactPermission,
      clientIp: DOMPurify.sanitize(window.akCIp),
      language: DOMPurify.sanitize(getCookie('lang')) || 'en_US',
      trialLength: trialLength || 14,
      dataResidency: getDataResidencyParam(),
      flowType: getFlowTypePayload(formType),
      formType,
      affiliateClickId: DOMPurify.sanitize(getCookie('clickid')),
      segExpQuery: segExpQuery || 'experiment_version',
      segExpValue: segExpValue || getExperimentVersion(),
    };

    if (!existingUserEmail) {
      const bossInvalid = await emailCheck(userEmail, signal);
      if (signal?.aborted) return bossInvalid;
      if (!bossInvalid.success) {
        if (bossInvalid.code === 2006) {
          retainFormData(formData);
        }
        return { success: false, code: bossInvalid.code };
      }
    }

    if (flowType === FORM_TYPES.PURCHASE) {
      await postTrialInterestBoomiService({
        businessMax,
        email: userEmail,
        contactPermission,
        trialSku: data.sku,
        programName: data.inputs?.programName?.defaultValue,
        programID: data.inputs?.programID?.defaultValue,
      });
    }

    const resp = await postTrialFormDataService(contactToken !== undefined ? { ...formData, contactToken } : formData);
    if (!resp.success) {
      if (resp.code === 2006) {
        retainFormData(formData);
      }
      return { success: false, code: resp.code };
    } else {
      gtagEnvelope('event', 'trial_success_business', {});
      facebookCAPIGTMEvent({
        contentId: 'lastpass_business',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phoneNumber,
      });
      await analyticsEvent(formData.email);
      if (flowType !== FORM_TYPES.PURCHASE) {
        formData.programName = data.inputs?.programName?.defaultValue;
        formData.programID = data.inputs?.programID?.defaultValue;
        if (webEnrichedFields !== undefined) {
          formData.webEnrichedFields = webEnrichedFields;
        }

        await postBoomiService({
          data: formData,
          userKey: resp.successToken,
        });

        if (isLoggedIn) {
          window.location.href = data!.successLoggedInUrl;
        } else {
          window.location.href = `${data.successUrl}?email=${formData.email}`;
        }
      } else {
        window.location.href = data.successUrl;
      }
    }
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      code: '500',
      errorMessage: `Trial submit error: ${error.message}`,
    };
  }
};
